// Alben (EE-Releases) — CMS-Verwaltung. Liste + Create/Edit-Form (showForm-Muster wie
// PlaylistManagement). Owner = EE-System; Authz macht das Backend. Plan: docs/plans/plan-albums.md.
import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { PlusIcon, PhotoIcon, RectangleStackIcon } from '@heroicons/react/24/outline';
import { albumService } from '../services/albumService';
import { AlbumForm } from '../components/forms/AlbumForm';
import { PageLayout } from '../components/ui/PageLayout';
import { Album, AlbumStatus } from '../types/album';
import { StatusBadge } from '../components/ui/StatusBadge';
import { Benachrichtigung } from '../components/ui/Benachrichtigung';
import { Filterleiste } from '../components/ui/Filterleiste';
import { SeitenblaetterFuss } from '../components/ui/SeitenblaetterFuss';
import { kuratierStatusAuswahl, seitenwechsel } from '../components/ui/filterZustand';
import { useBenachrichtigung } from '../hooks/useBenachrichtigung';
import { useSeitenausschnitt } from '../hooks/useSeitenausschnitt';

/**
 * Zeilen je Seite.
 *
 * ⚠️ Bewusst eine eigene Zahl und nicht die 10 der Audio-/Video-Listen: dort ist die Zahl eine
 * ANFRAGE-Größe (jede Seite kostet eine Runde zum Server), hier ist sie reine Anzeige — die Liste
 * liegt ohnehin vollständig im Speicher (siehe `useSeitenausschnitt`). Was zählt, ist deshalb
 * allein die Lesbarkeit der Tabelle, und dafür ist die 20 der Benutzer-Verwaltung das nähere Maß.
 */
const ZEILEN_JE_SEITE = 20;

/**
 * Der Filter-Zustand dieser Seite.
 *
 * ⚠️ `page` steckt bewusst im selben Objekt wie Suche und Status — nicht, weil es ein Filter wäre
 * (es ist keiner, siehe `seitenwechsel`), sondern damit `setzeFilter` unten die Seitenzahl
 * STRUKTURELL zurücksetzt. Läge sie daneben, müsste jeder einzelne Filter-Rückruf daran denken,
 * und einer würde es irgendwann nicht.
 */
interface AlbumFilter {
  page: number;
  suche: string;
  status: AlbumStatus | undefined;
}

const AlbumManagement: React.FC = () => {
  const [albums, setAlbums] = useState<Album[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<Album | null>(null);
  const [filter, setFilter] = useState<AlbumFilter>({ page: 1, suche: '', status: undefined });
  // `melde` trägt exakt die Signatur, die `AlbumForm` als `notify` erwartet — das Formular bleibt
  // deshalb unangetastet. Der frühere Handbau setzte seinen `setTimeout` ohne `clearTimeout` und
  // räumte auch Fehler nach vier Sekunden weg; beides liegt jetzt im Haken.
  const { inhalt, melde, schließe } = useBenachrichtigung();

  const load = useCallback(async () => {
    setIsLoading(true);
    try {
      setAlbums(await albumService.getAlbums());
    } catch {
      melde('error', 'Alben konnten nicht geladen werden');
    } finally {
      setIsLoading(false);
    }
  }, [melde]);

  useEffect(() => {
    void load();
  }, [load]);

  /**
   * Ein Filterwechsel holt die Liste an den Anfang zurück — `page: 1` steht deshalb HINTER dem
   * gesetzten Wert, nie davor. Genau andersherum stand es einmal auf der Inhalte-Seite und ließ
   * „Weiter" wieder Seite 1 laden (Herleitung bei `seitenwechsel` in `filterZustand.ts`).
   */
  const setzeFilter = <K extends keyof AlbumFilter>(feld: K, wert: AlbumFilter[K]) => {
    setFilter((vorher) => ({ ...vorher, [feld]: wert, page: 1 }));
  };

  /**
   * Gesucht wird über Titel UND Interpret — das sind die beiden Textspalten der Tabelle. Eine Suche,
   * die weniger durchsucht, als die Nutzerin sieht, findet ihr eigenes Suchergebnis nicht.
   *
   * ⚠️ KEINE Entprellung, anders als in `UserManagement`. Dort wartet das Suchfeld 300 ms, weil
   * jeder Anschlag sonst eine Anfrage auslöst; hier löst er gar keine aus — die Liste liegt im
   * Speicher. Eine Wartezeit wäre reine Trägheit ohne Gegenwert.
   */
  const gefiltert = useMemo(() => {
    const suche = filter.suche.trim().toLowerCase();
    return albums.filter((al) => {
      if (filter.status && al.status !== filter.status) return false;
      if (!suche) return true;
      return `${al.title} ${al.artist}`.toLowerCase().includes(suche);
    });
  }, [albums, filter.suche, filter.status]);

  const { ausschnitt, seite, seitenAnzahl, gesamt } = useSeitenausschnitt(
    gefiltert,
    ZEILEN_JE_SEITE,
    filter,
    setFilter,
  );

  const handleCreate = () => {
    setEditing(null);
    setShowForm(true);
  };

  const handleEdit = async (al: Album) => {
    try {
      const full = await albumService.getAlbumById(al.id);
      setEditing(full);
    } catch {
      setEditing(al);
    }
    setShowForm(true);
  };

  const handleClose = () => {
    setShowForm(false);
    setEditing(null);
    void load();
  };

  const handleDeleted = () => {
    setShowForm(false);
    setEditing(null);
    melde('success', 'Album gelöscht');
    void load();
  };

  if (showForm) {
    return (
      <PageLayout title={editing ? 'Album bearbeiten' : 'Neues Album'}>
        {/* Zweiter Aufrufort desselben Bausteins: der Formular-Zweig. Vorher standen hier und im
            Listen-Zweig zwei handgleiche Kopien — wer eine anfasste, sah die andere nicht.

            ⚠️ NUR HIER `schwebend`, nicht im Listen-Zweig unten — der Unterschied ist belegt, nicht
            Geschmack: `AlbumForm` öffnet den `ProductPicker`, ein Overlay über die ganze Fläche
            (`fixed inset-0 z-50 bg-black/50`). Sein `onAdd` läuft in `AlbumForm.applyContent`, und
            dessen `catch` meldet „Produkte konnten nicht aktualisiert werden" über genau dieses
            `notify` — ohne den Picker zu schließen (`ProductPicker.handleAdd` setzt nur `busyId`
            zurück). Im Fluss stünde die Meldung hinter dem abgedunkelten Overlay: die Nutzerin
            klickt auf ein Produkt, es erscheint nicht in der Liste, und sichtbar passiert nichts.
            Der Listen-Zweig hat kein Overlay und bleibt deshalb bei der Mehrheitsfassung. */}
        <Benachrichtigung inhalt={inhalt} onSchließen={schließe} schwebend />
        <AlbumForm
          key={editing?.id || 'new-album'}
          initial={editing}
          onCreated={(created) => setEditing(created)}
          onClose={handleClose}
          onDeleted={handleDeleted}
          notify={melde}
        />
      </PageLayout>
    );
  }

  return (
    <PageLayout
      title="Alben"
      subtitle="EE-Releases — Bündel von Subliminals."
      actions={
        <button onClick={handleCreate} className="btn btn-primary">
          <PlusIcon className="w-5 h-5 mr-2" />Neues Album
        </button>
      }
    >
      <Benachrichtigung inhalt={inhalt} onSchließen={schließe} />

      {/* ⚠️ Die Leiste hängt an `albums.length`, nicht an `gefiltert.length` — sonst verschwände
          sie mit dem letzten Treffer, und die Nutzerin stünde vor „Keine Alben gefunden" ohne das
          Feld, in dem der zu enge Suchbegriff steht. Bei einer wirklich leeren Verwaltung (noch
          gar kein Album angelegt) bleibt sie dagegen weg: dort gibt es nichts zu filtern. */}
      {albums.length > 0 && (
        <Filterleiste
          suche={filter.suche}
          onSuche={(wert) => setzeFilter('suche', wert)}
          platzhalter="Titel, Interpret..."
          auswahlen={[
            kuratierStatusAuswahl(filter.status, (wert) => setzeFilter('status', wert)),
          ]}
        />
      )}

      {isLoading ? (
        <div className="text-center py-12">
          <div className="loading-spinner mx-auto w-8 h-8"></div>
          <p className="mt-4 text-secondary-600">Lade Alben…</p>
        </div>
      ) : albums.length === 0 ? (
        <div className="text-center py-12 card">
          <RectangleStackIcon className="mx-auto h-12 w-12 text-secondary-400" />
          <p className="mt-4 text-secondary-600">Noch keine Alben</p>
          <button onClick={handleCreate} className="mt-4 btn btn-primary">
            <PlusIcon className="w-5 h-5 mr-2" />Erstes Album erstellen
          </button>
        </div>
      ) : gefiltert.length === 0 ? (
        /* ⚠️ Ein ZWEITER leerer Zustand, und zwar mit anderem Text und ohne „Erstes Album
           erstellen". Beides zusammengefasst hieße der Nutzerin zu sagen, es gebe keine Alben,
           während sie welche hat — und ihr als Ausweg das Anlegen eines weiteren anzubieten. */
        <div className="text-center py-12 card">
          <RectangleStackIcon className="mx-auto h-12 w-12 text-secondary-400" />
          <p className="mt-4 text-secondary-600">Keine Alben gefunden</p>
          <p className="mt-1 text-sm text-secondary-500">Suche oder Status anpassen.</p>
        </div>
      ) : (
        <div className="card overflow-x-auto">
          {/* Kopf- und Zellenmaße kommen aus `.table` (index.css) statt aus handkopierten
              Klassenketten. Was dabei bewusst aufgegeben wurde, steht dort am Regelblock —
              unter anderem der hellere Ton der Artist-Spalte. */}
          <table className="table">
            <thead>
              <tr>
                <th>Cover</th>
                <th>Titel</th>
                <th>Artist</th>
                <th>Produkte</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {ausschnitt.map((al) => (
                <tr key={al.id}>
                  <td>
                    <button type="button" onClick={() => void handleEdit(al)} title="Bearbeiten" className="focus:outline-none">
                      {al.coverImageUrl ? (
                        <img src={al.coverImageUrl} alt={al.title} className="h-10 w-10 rounded object-cover" />
                      ) : (
                        <div className="h-10 w-10 rounded bg-secondary-100 flex items-center justify-center"><PhotoIcon className="w-5 h-5 text-secondary-400" /></div>
                      )}
                    </button>
                  </td>
                  <td>
                    <button type="button" onClick={() => void handleEdit(al)} className="text-sm font-medium text-primary-600 hover:text-primary-800 hover:underline text-left focus:outline-none">
                      {al.title}
                    </button>
                  </td>
                  <td>{al.artist}</td>
                  <td>{al.itemCount}</td>
                  <td><StatusBadge status={al.status} /></td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* ⚠️ `gesamt` ist die Zahl der TREFFER, nicht die aller Alben. Bei gesetztem Filter
              stünde sonst „12 Einträge" unter einer Tabelle, die drei zeigt und auch nur drei
              hat. Der Rücksprung aus einer leergewordenen Seite steckt in `useSeitenausschnitt`
              und nicht hier — diese Leiste steht gar nicht im Baum, wenn die Liste leer ist. */}
          <SeitenblaetterFuss
            seite={seite}
            seitenAnzahl={seitenAnzahl}
            gesamt={gesamt}
            onSeitenwechsel={seitenwechsel(setFilter)}
          />
        </div>
      )}
    </PageLayout>
  );
};

export default AlbumManagement;
